import {Component, OnInit, OnDestroy, signal} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import {UserApiService} from 'src/app/core/api/user-api.service';
import {Role} from 'src/app/core/model/role';
import {User} from 'src/app/core/model/user';
import {GetActiveUserAction, SetActiveUserRole} from 'src/app/core/store/active-user/active-user.action';
import {GetAllRolesAction} from 'src/app/core/store/roles/roles.action';
import {selectRouterQueryParam} from 'src/app/core/store/router/router.selector';
import {GetAllUsersSuccessAction} from 'src/app/core/store/users/users.action';
import {PrivacyPolicyDialogComponent} from 'src/app/shared/dialog/privacy-policy-dialog/privacy-policy-dialog.component';
import {TitleService} from '../../core/page/title.service';
import {AuthenticationService} from '../auth.service';
import {emailOrAdminValidator} from './email-or-admin-validator';
import {UpdateSkedContextAction} from 'src/app/core/store/current-sked/current-sked.action';
import {MobileService} from 'src/app/core/page/mobile.service';
import {updateApiUrl} from 'src/app/shared/utils/api-url.util';
import {
  API_ENVIRONMENTS,
  API_ENVIRONMENT_NAMES,
  ENVIRONMENT_NAMES,
} from '../../../environments/api-environments.constants';
import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  errorMsg: string;
  emailSentMessage: string;
  isMobile = signal(this.mobileService.isMobilePlatform());

  view$: Observable<'forgotPassword' | 'emailSent'>;
  authenticated$: Observable<boolean>;

  public passwordInputType$ = new BehaviorSubject<string>('password');

  isMobileApp: boolean = false;
  // All available API endpoints for native app override selector
  isLocalHostEnvironment = signal<boolean>(environment.envName === ENVIRONMENT_NAMES.LOCALHOST);
  apiEnvironments = API_ENVIRONMENTS;
  private readonly defaultNativeApiUrl =
    API_ENVIRONMENTS.find(env => env.name === API_ENVIRONMENT_NAMES.DEV)?.url || environment.apiUrl;
  selectedApiUrl: string = environment.apiUrl;

  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: TitleService,
    private userApiService: UserApiService,
    private store$: Store,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private mobileService: MobileService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit() {
    this.titleService.setPageTitle('Login');
    this.view$ = this.store$.pipe(select(selectRouterQueryParam('view')));
    this.isMobileApp = this.mobileService.isMobilePlatform();

    // On mobile apps, clear stored API URL and default to Dev environment
    if (this.isMobileApp) {
      localStorage.removeItem('apiUrl');
      this.selectedApiUrl = this.defaultNativeApiUrl;
      updateApiUrl(this.selectedApiUrl);
    }

    this.loginForm = this.formBuilder.group({
      email: ['', emailOrAdminValidator()],
      password: [''],
      requestEmail: ['', Validators.email],
      apiUrl: [this.selectedApiUrl],
    });

    if (this.isMobileApp) {
      this.subscribeToApiUrlChanges();
    }

    // reset login status
    this.authenticationService.logout(true);
    // get return url from route parameters or default to '/'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/current-sked';
    this.authenticated$ = this.authenticationService.isLoggedIn;
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  // This is only for the environment selector in the mobile app,
  // which allows switching API endpoints without restarting the app
  private subscribeToApiUrlChanges(): void {
    const apiUrlControl = this.loginForm.get('apiUrl');
    if (apiUrlControl) {
      apiUrlControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(apiUrl => {
        if (apiUrl) {
          this.selectedApiUrl = apiUrl;
          updateApiUrl(apiUrl);
        }
      });
    }
  }

  handleKeyDown(event: KeyboardEvent, view: string) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!view) {
        this.onGo(view);
      } else if (view === 'forgotPassword') {
        this.onRequestPasswordReset(view);
      } else {
        this.changeView(null);
      }
    }
  }
  changeView(view: null | 'forgotPassword' | 'emailSent') {
    this.errorMsg = '';
    this.loading = false;
    this.router.navigate([], {queryParams: {view}, relativeTo: this.activatedRoute});
  }

  onGo(view: string | null) {
    this.submitted = true;
    if (!view && !this.f.email.invalid) {
      this.errorMsg = 'Valid email is required';
    }
    // stop here if form is invalid
    if (!view && (!this.f.email.value || !this.f.password.value)) {
      this.errorMsg = 'Email and password must be filled in';
      return;
    }

    if (this.isMobileApp && this.f.apiUrl.value) {
      this.selectedApiUrl = this.f.apiUrl.value;
      updateApiUrl(this.selectedApiUrl);
    }

    this.errorMsg = '';
    this.loading = true;
    this.authenticationService
      .login(this.f.email.value.toLowerCase(), this.f.password.value)
      .pipe(first())
      .subscribe({
        next: data => {
          this.userApiService.getAll().subscribe(users => {
            this.store$.dispatch(new GetAllUsersSuccessAction({users}));
            const user = users.find(u => u.email === data.email);
            if (user) {
              this.store$.dispatch(
                new UpdateSkedContextAction({
                  skedContext: {type: 'sked', projectId: '', preventGetSkedData: false, filter: 'today'},
                })
              );
              this.store$.dispatch(new GetActiveUserAction({}));
              this.store$.dispatch(new GetAllRolesAction({onSuccess: roles => this.onGetRolesSuccess(roles, user)}));
            }
          });
        },
        error: error => {
          if (error === 'Connection to the Database lost') {
            this.errorMsg = error;
          } else {
            this.errorMsg = 'Incorrect email/password. Please try again.';
          }
          this.loading = false;
        },
      });
  }

  onGetRolesSuccess(roles: Role[], activeUser: User) {
    const role = roles?.find(r => activeUser?.roles[0]._id === r.id);
    if (role) {
      this.store$.dispatch(new SetActiveUserRole({role}));
    }
    this.router.navigateByUrl(this.returnUrl);
  }

  public onTogglePassword(event: MouseEvent) {
    event.preventDefault();
    const type = this.passwordInputType$.value === 'password' ? 'text' : 'password';
    this.passwordInputType$.next(type);
  }

  onRequestPasswordReset(view: string) {
    if (view === 'forgotPassword' && (!this.f.requestEmail.value || this.f.requestEmail.invalid)) {
      this.errorMsg = 'You must enter a valid email address';
      return;
    }
    this.errorMsg = '';
    this.loading = true;
    const url = `${window.location.protocol}//${window.location.host}/reset-password`;
    this.authenticationService
      .requestPasswordReset(this.f.requestEmail.value, url)
      .pipe(first())
      .subscribe({
        next: data => {
          this.loginForm.patchValue({requestEmail: ''});
          this.emailSentMessage = data;
          this.router.navigate([], {queryParams: {view: 'emailSent'}, relativeTo: this.activatedRoute});
        },
        error: error => {
          this.errorMsg = error || 'There was a problem.';
          this.loading = false;
        },
      });
  }

  onPrivacyPolicy() {
    this.dialog.open(PrivacyPolicyDialogComponent, {
      panelClass: 'privacy-policy-dialog',
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      disableClose: false,
    });
  }
}
